// Shared legal / support content, rendered on both the web and the Flutter
// app from the SAME source: src/content/legal.json is a copy of
// ../../app/assets/content/legal.json (canonical). Regenerate with
// `make sync-legal` in ../app — do not hand-edit the copy here.

import data from '../content/legal.json';

export type LegalLocale = 'en' | 'de' | 'fr' | 'it' | 'es' | 'pt';
export const LEGAL_LOCALES: LegalLocale[] = ['en', 'de', 'fr', 'it', 'es', 'pt'];

export type LegalBlock =
  | { type: 'p' | 'h3' | 'h4'; text: string }
  | { type: 'ul'; items: string[] };

export type LegalLocaleContent = { title: string; body: LegalBlock[] };
export type LegalDoc = { updated: string; locales: Record<string, LegalLocaleContent> };

export type LegalNav = { privacy: string; terms: string; support: string; deleteAccount: string };
export type LegalChrome = {
  lastUpdated: string;
  englishGoverns: string;
  home: string;
  close: string;
  nav: LegalNav;
};

export type LegalBundle = {
  version: number;
  locales: LegalLocale[];
  chrome: Record<string, LegalChrome>;
  documents: Record<string, LegalDoc>;
};

export const legal = data as unknown as LegalBundle;

/** Best browser-language match among our supported locales, else English. */
export function pickLocale(): LegalLocale {
  const nav =
    (typeof navigator !== 'undefined' ? navigator.language : 'en')
      .slice(0, 2)
      .toLowerCase();
  return (LEGAL_LOCALES as string[]).includes(nav) ? (nav as LegalLocale) : 'en';
}

export function chromeFor(locale: string): LegalChrome {
  return legal.chrome[locale] ?? legal.chrome.en;
}

export function contentFor(doc: LegalDoc, locale: string): LegalLocaleContent {
  return doc.locales[locale] ?? doc.locales.en;
}

export function formatUpdated(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso));
}

export type Inline =
  | { kind: 'text' | 'bold'; text: string }
  | { kind: 'link'; text: string; url: string };

/**
 * Parses the same minimal inline markup the Flutter renderer understands:
 * `**bold**` and `[label](url)`. Unmatched markers stay literal.
 */
export function parseInline(input: string): Inline[] {
  const out: Inline[] = [];
  let buf = '';
  const flush = () => {
    if (buf) {
      out.push({ kind: 'text', text: buf });
      buf = '';
    }
  };

  let i = 0;
  while (i < input.length) {
    if (input.startsWith('**', i)) {
      const end = input.indexOf('**', i + 2);
      if (end !== -1) {
        flush();
        out.push({ kind: 'bold', text: input.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }
    if (input[i] === '[') {
      const close = input.indexOf(']', i + 1);
      if (close !== -1 && input[close + 1] === '(') {
        const paren = input.indexOf(')', close + 2);
        if (paren !== -1) {
          flush();
          out.push({
            kind: 'link',
            text: input.slice(i + 1, close),
            url: input.slice(close + 2, paren),
          });
          i = paren + 1;
          continue;
        }
      }
    }
    buf += input[i];
    i++;
  }
  flush();
  return out;
}
